import { useCallback, useState } from 'react'

import {
  EventRegistration,
  QueryEventRegistrationsArgs,
} from '@tribeplatform/gql-client/types'
import type { EventRegistrationFields } from '@tribeplatform/gql-client/types/generated'

import { useTribeClient } from '../../useTribeClient.js'

const MAX_PAGES = 500

export function useExportEventRegistrations(options: {
  variables: Pick<QueryEventRegistrationsArgs, 'eventId'>
  fields?: EventRegistrationFields
  onError?: () => void
  onSuccess?: (items: EventRegistration[]) => void
}) {
  const { client } = useTribeClient()
  const [isLoading, setIsLoading] = useState(false)

  const exportCSV = useCallback(async () => {
    if (isLoading) {
      return
    }
    setIsLoading(true)

    try {
      const { variables, fields } = options
      let after: string | undefined
      let hasNextPage = true
      let page = 0
      const allRegistrations: EventRegistration[] = []

      while (hasNextPage && page < MAX_PAGES) {
        // eslint-disable-next-line no-await-in-loop
        const result = await client.events.eventRegistrations(
          { ...variables, after, limit: 20 },
          fields || { member: 'basic' },
        )
        allRegistrations.push(...result.nodes)

        const nextCursor = result.pageInfo?.endCursor
        if (nextCursor === after) {
          break
        }

        after = nextCursor
        hasNextPage = !!result.pageInfo?.hasNextPage
        page += 1
      }

      if (page >= MAX_PAGES) {
        throw new Error('Export exceeded maximum page limit')
      }

      options.onSuccess?.(allRegistrations)
    } catch {
      options.onError?.()
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, client.events, options])

  return { exportCSV, isLoading }
}
